import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Palmtree, CheckCircle2, XCircle, Clock, Loader2, User, Calendar, MessageSquare, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const leaveTypeColor = (t) => {
  switch (t) {
    case 'Annual Leave': return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'Sick Leave': return 'bg-red-100 text-red-800 border-red-300';
    case 'Maternity Leave': return 'bg-pink-100 text-pink-800 border-pink-300';
    case 'Paternity Leave': return 'bg-indigo-100 text-indigo-800 border-indigo-300';
    case 'Unpaid Leave': return 'bg-gray-100 text-gray-800 border-gray-300';
    default: return 'bg-slate-100 text-slate-800 border-slate-300';
  }
};

const TeamLeave = () => {
  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState({}); // employee_id -> balance object
  const [loading, setLoading] = useState(true);
  const [actionTarget, setActionTarget] = useState(null); // { request, action: 'approve'|'reject' }
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchTeamRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leave/team-requests`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      } else {
        toast.error('Failed to load team leave requests');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchBalanceFor = useCallback(async (employeeId) => {
    try {
      const res = await fetch(`${API_URL}/api/leave/team-balance/${employeeId}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setBalances((prev) => ({ ...prev, [employeeId]: data }));
      }
    } catch (e) { console.error('Balance fetch failed', e); }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => { fetchTeamRequests(); }, [fetchTeamRequests]);

  // Fetch balance for each unique employee in pending list
  useEffect(() => {
    const uniqueEmployees = Array.from(new Set(requests.map((r) => r.employee_id)));
    uniqueEmployees.forEach((id) => {
      if (id && !balances[id]) fetchBalanceFor(id);
    });
  }, [requests]);

  const submitApprove = async () => {
    if (!actionTarget) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leave/request/${actionTarget.request.id}/manager-approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Request approved — forwarded to HR');
        setActionTarget(null);
        setRejectionReason('');
        fetchTeamRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to approve');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!actionTarget) return;
    if (!rejectionReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams({ rejection_reason: rejectionReason.trim() });
      const res = await fetch(
        `${API_URL}/api/leave/request/${actionTarget.request.id}/manager-reject?${params.toString()}`,
        { method: 'PUT', headers: getAuthHeaders() },
      );
      if (res.ok) {
        toast.success('Request rejected');
        setActionTarget(null);
        setRejectionReason('');
        fetchTeamRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to reject');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="team-leave-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Palmtree className="h-6 w-6 text-emerald-600" />
          Team Leave
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Approve or reject leave requests from your direct reports. Approved requests are forwarded to HR for final approval.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{requests.length}</span>
            <span className="text-muted-foreground">pending request{requests.length !== 1 ? 's' : ''}</span>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading team requests...
        </CardContent></Card>
      ) : requests.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <Palmtree className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No pending leave requests</p>
          <p className="text-sm">When your direct reports submit leave applications, they will appear here for your review.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {requests.map((r) => {
            const bal = balances[r.employee_id];
            const annual = bal?.annual_leave;
            const sick = bal?.sick_leave;
            return (
              <Card key={r.id} className="overflow-hidden" data-testid={`team-leave-card-${r.id}`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {r.employee_name || 'Employee'}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={leaveTypeColor(r.leave_type)}>{r.leave_type}</Badge>
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending Manager
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Days Requested</div>
                      <div className="text-2xl font-bold">{r.days_requested}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-muted/30 rounded-lg p-3">
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Start Date</div>
                      <div className="font-medium flex items-center gap-1.5 mt-0.5">
                        <Calendar className="h-3.5 w-3.5 opacity-60" />{r.start_date}
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">End Date</div>
                      <div className="font-medium flex items-center gap-1.5 mt-0.5">
                        <Calendar className="h-3.5 w-3.5 opacity-60" />{r.end_date}
                      </div>
                    </div>
                  </div>

                  {r.reason && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 rounded-lg p-3">
                      <div className="text-[11px] text-blue-700 dark:text-blue-300 uppercase tracking-wide font-medium flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> Reason
                      </div>
                      <div className="text-sm text-blue-900 dark:text-blue-100 mt-1">{r.reason}</div>
                    </div>
                  )}

                  {bal && (
                    <div className="border rounded-lg p-3 bg-muted/10">
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium mb-2">
                        Current Leave Balance ({bal.year})
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {annual && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Annual</span>
                            <span className="font-medium">
                              {annual.remaining}/{annual.total} days
                            </span>
                          </div>
                        )}
                        {sick && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Sick</span>
                            <span className="font-medium">
                              {sick.remaining}/{sick.entitled} days
                            </span>
                          </div>
                        )}
                        {bal.maternity_leave && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Maternity</span>
                            <span className="font-medium">
                              {bal.maternity_leave.remaining}/{bal.maternity_leave.entitled} days
                            </span>
                          </div>
                        )}
                        {bal.paternity_leave && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Paternity</span>
                            <span className="font-medium">
                              {bal.paternity_leave.remaining}/{bal.paternity_leave.entitled} days
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between col-span-2 pt-1 border-t mt-1">
                          <span className="text-muted-foreground">Pending requests</span>
                          <span className="font-medium">{bal.pending_requests}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      onClick={() => setActionTarget({ request: r, action: 'approve' })}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                      data-testid={`approve-leave-${r.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActionTarget({ request: r, action: 'reject' })}
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50 gap-2"
                      data-testid={`reject-leave-${r.id}`}
                    >
                      <XCircle className="h-4 w-4" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!actionTarget}
        onOpenChange={(o) => { if (!o) { setActionTarget(null); setRejectionReason(''); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionTarget?.action === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{actionTarget?.request?.employee_name}</span>
              {' • '}
              {actionTarget?.request?.leave_type}
              {' • '}
              {actionTarget?.request?.days_requested} day(s)
            </p>
            <p className="text-sm">
              {actionTarget?.action === 'approve'
                ? 'Approving this request will forward it to HR for final approval.'
                : 'Rejecting this request will end the application. Please provide a reason for the employee.'}
            </p>
            {actionTarget?.action === 'reject' && (
              <Textarea
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Reason for rejection..."
                data-testid="rejection-reason-input"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionTarget(null); setRejectionReason(''); }} disabled={submitting}>
              Cancel
            </Button>
            {actionTarget?.action === 'approve' ? (
              <Button onClick={submitApprove} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-btn">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Approve
              </Button>
            ) : (
              <Button onClick={submitReject} disabled={submitting} variant="destructive" data-testid="confirm-reject-btn">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Reject
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamLeave;
